import JSZip from 'jszip';
import { useCallback, useRef, useState } from 'react';
import { formatFileSize, parseJSON } from '@/shared/commonFunctions';
import { useEvents, getDateTimeMS } from '../use-events/useEvents';
import { unregisterServiceWorker } from '@/shared/serviceWorkFunctions';
import { getOnStorage, removeFromStorage, setOnStorage } from '@/shared';
import { useClient, useFetchAxios, useGlobals, useUserContext } from '@/hooks';
import { Tables, toCoded, useDbFunctions } from '../dbFunctions/useDBFunctions';
import {
  tableKeys,
  TableTypes,
  CategoryProps,
  DataSetsProps,
  ProductProps,
  CustomerDataProps,
  EventsClientProps,
  PromocaoClientProps,
  DicUltimaCompraProps,
  CampanhaProdutosProps,
  PrecoPromocaoClienteProps,
  ClientProps,
  PageBuilderProps,
  SalesCycleProps,
} from '@/types';

type CatalogDictProps = Record<number, ProductProps>;
type CategoryDictProps = Record<number, CategoryProps>;
type DownloadEventProps = Record<string, { status: 0 | 1, arquivo?: string, dataFim?: string, dataInicio?: string }>;

type DownloadMemProfile = {
  setDbBatchSize: number;
  catalogChunkSize: number;
  clientsChunkSize: number;
  maxConcurrentDownloads: number;
  /** Uma escrita IndexedDB por vez dentro do lote — menor pico de RAM, sync mais lento. */
  sequentialDbWrites: boolean;
};

const DEFAULT_MEM_PROFILE: DownloadMemProfile = {
  setDbBatchSize: 5,
  catalogChunkSize: 400,
  clientsChunkSize: 300,
  maxConcurrentDownloads: 2,
  sequentialDbWrites: false,
};

/**
 * Em aparelhos com pouca RAM, sync grande + ZIP + IDB pode estourar memória e o SO mata o PWA.
 * `navigator.deviceMemory` (GB) existe no Chrome/Android; Safari costuma não expor — use
 * `localStorage.setItem('pwa_sync_low_memory','1')` no mesmo origin para forçar perfil mínimo.
 */
function getDownloadMemoryTuning(): DownloadMemProfile {
  try {
    if (typeof localStorage !== 'undefined' && localStorage.getItem('pwa_sync_low_memory') === '1') {
      return {
        setDbBatchSize: 2,
        catalogChunkSize: 150,
        clientsChunkSize: 120,
        maxConcurrentDownloads: 1,
        sequentialDbWrites: true,
      };
    }
  } catch {
    /* storage indisponível */
  }

  if (typeof navigator === 'undefined') return DEFAULT_MEM_PROFILE;

  const gb = (navigator as Navigator & { deviceMemory?: number }).deviceMemory;
  if (gb !== undefined && gb <= 2) {
    return {
      setDbBatchSize: 2,
      catalogChunkSize: 150,
      clientsChunkSize: 120,
      maxConcurrentDownloads: 1,
      sequentialDbWrites: true,
    };
  }
  if (gb !== undefined && gb <= 4) {
    return {
      setDbBatchSize: 3,
      catalogChunkSize: 280,
      clientsChunkSize: 220,
      maxConcurrentDownloads: 1,
      sequentialDbWrites: false,
    };
  }
  return DEFAULT_MEM_PROFILE;
}

const _memTune = getDownloadMemoryTuning();
const SET_DB_BATCH_SIZE = _memTune.setDbBatchSize;
const CATALOG_CHUNK_SIZE = _memTune.catalogChunkSize;
const CLIENTS_CHUNK_SIZE = _memTune.clientsChunkSize;
const MAX_CONCURRENT_DOWNLOADS = _memTune.maxConcurrentDownloads;
const SEQUENTIAL_IDB_BATCH = _memTune.sequentialDbWrites;
const PROGRESS_THROTTLE_MS = 150;
const GETDATASET_TIMEOUT_MS = 45000;
const SPEED_UPDATE_INTERVAL_MS = 500;
const GETDATASET_STOCK_TIMEOUT_MS = 15000;

const progressLastCall: Record<string, number> = {};
const progressPercentSetterRef: { current: (n: number) => void } = { current: () => {} };

const runWithConcurrency = async <T>(tasks: (() => Promise<T>)[], max: number): Promise<PromiseSettledResult<T>[]> => {
  const results: PromiseSettledResult<T>[] = [];
  let index = 0;
  const runNext = async (): Promise<void> => {
    const i = index++;
    if (i >= tasks.length) return;
    try {
      const value = await tasks[i]();
      results[i] = { status: 'fulfilled', value };
    } catch (reason) {
      results[i] = { status: 'rejected', reason };
    }
    await runNext();
  };
  const workers = Array.from({ length: Math.min(max, tasks.length) }, () => runNext());
  await Promise.all(workers);
  return results;
};

const mountCategories = (campanha: CampanhaProdutosProps) => {
  if (!campanha) return [];

  return [
    {
      NRO_HIERARQUIA: 1,
      DE_CATEGORIA: campanha.nome,
      IMAGE: campanha?.inativo?.selo || '',
      PATH_CATEGORIA: campanha.url_amigavel,
    },
  ] as CategoryProps[];
};

const updateProgress = (loaded: number, total: number, name: string) => {
  const now = Date.now();
  if (progressLastCall[name] != null && now - progressLastCall[name] < PROGRESS_THROTTLE_MS) return;
  progressLastCall[name] = now;

  const percentual = total > 0 ? Math.min((loaded / total) * 100, 100) : 0;
  const progressBar = document.getElementById(`${name}-bar`) as HTMLElement;
  const progressText = document.getElementById(`${name}-text`) as HTMLElement;
  const isValid = Number.isFinite(percentual);

  if (name === 'progress') {
    progressPercentSetterRef.current(percentual);
  } else {
    if (progressBar) progressBar.style.width = `${percentual}%`;
  }
  if (progressText) progressText.textContent = isValid ? `${Math.round(percentual)}%` : 'Erro no Download';
};

const getProductCodeFromFilename = (filename: string) => {
  const onlyNumbers = filename.split('/').pop()?.replace(/\D+/g, '');
  const code = Number(onlyNumbers);
  return Number.isFinite(code) ? code : null;
};

const isZipContent = (resp: Response, url: string) => {
  const ct = resp.headers.get('content-type') || '';
  return ct.includes('zip') || url.toLowerCase().endsWith('.zip');
};

const yieldToMain = (): Promise<void> =>
  new Promise((resolve) => {
    if (typeof requestIdleCallback !== 'undefined') {
      requestIdleCallback(() => resolve(), { timeout: 50 });
    } else {
      setTimeout(resolve, 0);
    }
});

const blobToBase64 = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(String(reader.result));
    reader.onerror = reject;
    reader.readAsDataURL(blob);
});

const DATASET_TYPE_MAP: Record<string, string> = {
  'imagens_zip': 'imagens',
};

const normalizeDatasetType = (tipo: string) => DATASET_TYPE_MAP[tipo] || tipo;

export const useDownloadFiles = () => {
  let isLoaded = false;
  let mapedPrices: any = {};
  let products: ProductProps[] = [];
  let eventos: EventsClientProps = {};
  let sortedClients: ClientProps[] = [];
  let allCategories: CategoryProps[] = [];
  let pageBuilder: PageBuilderProps[] = [];
  let promocaoCliente: PrecoPromocaoClienteProps = {};

  const objProduct = {} as CatalogDictProps;
  const objCategory = {} as CategoryDictProps;
  const productUltimaCompra: DicUltimaCompraProps = {};
  const cycleByClient: Record<number, SalesCycleProps[]> = {};
  const clientes: Record<number, (ClientProps & { CYCLE: SalesCycleProps[] })> = {};

  const loading = useRef(false);
  const setDownloadSpeedBpsRef = useRef<(bps: number) => void>(() => {});
  const downloadLabelTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const displayLeaderRef = useRef<string | null>(null);

  const [hasError, setError] = useState(false);
  const [preparing, setPreparing] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const fileName = '';
  const [isOpenedModal, openModalSync] = useState(false);
  const [noUpdate, setNoUpdate] = useState(false as Boolean);
  const [datasets, setDataSets] = useState([] as DataSetsProps[]);

  const { axiosCtx } = useFetchAxios();
  const db = useDbFunctions();
  const { registerEvent } = useEvents(db);
  const { setCustomer } = useUserContext();
  const setOnDB = db.setOnDB;
  const getOnDB = db.getOnDB;
  const { setCampanhaProdutos, setCampanhaClientes, setCampanhaFaixa, setCampanhaTopMix, setIndicadorLoja, setIndicadoresProdutividade } = useClient();
  const { setLastSync, getDate, setDownloadingFileName, setDownloadingFiles, setStartDownloadingFile, downloadSpeedBps, setDownloadSpeedBps, setFileDownload, setFileDownloadProgress, setProgressPercent } = useGlobals();
  progressPercentSetterRef.current = setProgressPercent;
  setDownloadSpeedBpsRef.current = setDownloadSpeedBps;

  const handleDownloadingFileName = useCallback(() => {
    if (isSuccess || !isOpenedModal) {
      if (downloadLabelTimeoutRef.current) clearTimeout(downloadLabelTimeoutRef.current);
      return;
    }

    const listDataBase = getOnStorage('datasets');
    const ind = Math.floor(Math.random() * (listDataBase?.length || 0));
    let label = listDataBase?.[ind]?.DESC_ARQUIVO;
    if (label === fileName) {
      label = listDataBase?.[ind + 1]?.DESC_ARQUIVO || 'Baixando';
    }

    downloadLabelTimeoutRef.current = setTimeout(() => handleDownloadingFileName(), 3000);
  }, [fileName, isOpenedModal, isSuccess]);

  const fetchDatasets = useCallback(
    async (list?: DataSetsProps[]) => {
      var totalSize = 0;
      var downloadedSize = 0;
      var speedLastTime = Date.now();
      var speedLastBytes = 0;
      setDownloadSpeedBpsRef.current(0);
      const downloadEvents = {} as DownloadEventProps;

      const fetchAndSaveJson = async (
        url: string,
        name: string,
        desc: string,
        skipZipRedirect = false
      ): Promise<any> => {
        if (!displayLeaderRef.current) displayLeaderRef.current = desc;
        if (displayLeaderRef.current === desc) {
          setDownloadingFileName(desc);
          setFileDownloadProgress(formatFileSize(0));
        }

        downloadEvents[name] = { ...(downloadEvents[name] || {}), arquivo: name, dataInicio: getDateTimeMS(), status: 1 };
        setStartDownloadingFile(name);

        try {
          const resp = await fetch(url, { cache: 'no-store' });
          
          if (!skipZipRedirect && isZipContent(resp, url)) {
            console.warn(`[${name}] conteúdo ZIP detectado, redirecionando para fetchAndSaveImages`);
            return fetchAndSaveImages(url, name, desc);
          }
          
          if (resp.status >= 300 || resp.status < 200) {
            registerEvent({
              stEvento: 0,
              tipoEvento: 'DOWNLOAD',
              obsEvento: `Failed to fetch ${name}: ${resp.statusText}`,
            });
            throw new Error(`Failed to fetch ${name}: ${resp.statusText}`);
          }
          
          const contentLength = Number(resp.headers.get('content-length')) || 0;
          totalSize += contentLength;
          let totalArchive = contentLength;
          let downloadedArchive = 0;
          let lastFileDownloadUpdate = 0;

          if (displayLeaderRef.current === desc) {
            setFileDownload(formatFileSize(totalArchive));
            setFileDownloadProgress(formatFileSize(0));
            setProgressPercent(0);
          }

          const reader = resp?.body?.getReader();
          if (!reader) throw new Error('Failed to get response body');
          const decoder = new TextDecoder('utf-8');
          const chunks: string[] = [];

          while (true) {
            const result = await reader.read();
            const { done, value } = result;
            if (done) break;
            const len = value?.byteLength ?? 0;
            downloadedSize += len || 1;
            downloadedArchive += len || 1;
            const now = Date.now();
            if (now - speedLastTime >= SPEED_UPDATE_INTERVAL_MS) {
              const elapsed = (now - speedLastTime) / 1000;
              if (elapsed > 0) {
                const bps = (downloadedSize - speedLastBytes) / elapsed;
                setDownloadSpeedBpsRef.current(bps);
              }
              speedLastTime = now;
              speedLastBytes = downloadedSize;
            }
            if (!displayLeaderRef.current) {
              displayLeaderRef.current = desc;
              setDownloadingFileName(desc);
            }
            if (now - lastFileDownloadUpdate >= PROGRESS_THROTTLE_MS && displayLeaderRef.current === desc) {
              lastFileDownloadUpdate = now;
              setFileDownload(formatFileSize(totalArchive));
              setFileDownloadProgress(formatFileSize(downloadedArchive));
            }
            updateProgress(downloadedArchive, totalArchive, name);
            if (displayLeaderRef.current === desc) updateProgress(downloadedArchive, totalArchive, 'progress');
            if (len) chunks.push(decoder.decode(value, { stream: true }));
          }
          progressLastCall[name] = 0;
          progressLastCall['progress'] = 0;
          if (displayLeaderRef.current === desc) {
            setFileDownload(formatFileSize(totalArchive));
            setFileDownloadProgress(formatFileSize(downloadedArchive));
            updateProgress(downloadedArchive, totalArchive, 'progress');
          }
          updateProgress(downloadedArchive, totalArchive, name);

          if (displayLeaderRef.current === desc) displayLeaderRef.current = null;

          const json = JSON.parse(chunks.join(''));
          chunks.length = 0;
          let data = json?.catalogo ?? json?.preco ?? json;

          if (name === 'imagens') {
            data = typeof data === 'string' ? JSON.parse(data) : data;
            const out: Record<number, string> = {};
            if (Array.isArray(data)) {
              for (let j = 0; j < data.length; j++) {
                const item = data[j];
                out[item.COD_PRODUTO] = item.imagem?.replace(/^data:.*;base64,/i, '');
              }
            }
            data = out;
          }
          if (typeof data === 'string') data = JSON.parse(data);
          downloadEvents[name] = { ...(downloadEvents[name] || {}), dataFim: getDateTimeMS(), status: 1 };
          return { data, name };
        } catch (error) {
          if (displayLeaderRef.current === desc) displayLeaderRef.current = null;
          console.error(`Error fetching ${name}:`, error);
          setError(true);
          downloadEvents[name] = { ...(downloadEvents[name] || {}), dataFim: getDateTimeMS(), status: 0 };
          throw error;
        }
      };

      const fetchAndSaveImages = async (url: string, name: string, desc: string) => {
        if (!displayLeaderRef.current) displayLeaderRef.current = desc;
        if (displayLeaderRef.current === desc) {
          setDownloadingFileName(desc);
          setFileDownloadProgress(formatFileSize(0));
        }
        downloadEvents[name] = { ...(downloadEvents[name] || {}), arquivo: name, dataInicio: getDateTimeMS(), status: 1 };

        setStartDownloadingFile(name);

        console.log('[images] starting download', { url });

        try {
          const resp = await fetch(url, { cache: 'no-store' });
          console.log('[images] response', { status: resp.status, ok: resp.ok, contentType: resp.headers.get('content-type') });

          if (!resp.ok) throw new Error(`Failed to fetch imagens zip: ${resp.statusText}`);
          if (!isZipContent(resp, url)) throw new Error('Conteúdo não é ZIP, fallback para JSON');

          const contentLength = Number(resp.headers.get('content-length')) || 0;
          totalSize += contentLength;
          let totalArchive = contentLength;
          let downloadedArchive = 0;
          let lastFileDownloadUpdate = 0;

          if (displayLeaderRef.current === desc) {
            setFileDownload(formatFileSize(totalArchive));
            setFileDownloadProgress(formatFileSize(0));
            setProgressPercent(0);
          }

          const reader = resp.body?.getReader();
          if (!reader) throw new Error('Failed to get response body');
          const chunks: Uint8Array[] = [];

          while (true) {
            const result = await reader.read();
            const { done, value } = result;
            if (done) break;
            const len = value?.byteLength ?? 0;
            downloadedSize += len || 1;
            downloadedArchive += len || 1;
            const now = Date.now();
            if (now - speedLastTime >= SPEED_UPDATE_INTERVAL_MS) {
              const elapsed = (now - speedLastTime) / 1000;
              if (elapsed > 0) {
                const bps = (downloadedSize - speedLastBytes) / elapsed;
                setDownloadSpeedBpsRef.current(bps);
              }
              speedLastTime = now;
              speedLastBytes = downloadedSize;
            }
            if (!displayLeaderRef.current) {
              displayLeaderRef.current = desc;
              setDownloadingFileName(desc);
            }
            if (now - lastFileDownloadUpdate >= PROGRESS_THROTTLE_MS && displayLeaderRef.current === desc) {
              lastFileDownloadUpdate = now;
              setFileDownload(formatFileSize(totalArchive));
              setFileDownloadProgress(formatFileSize(downloadedArchive));
            }
            updateProgress(downloadedArchive, totalArchive, name);
            if (displayLeaderRef.current === desc) updateProgress(downloadedArchive, totalArchive, 'progress');
            if (value) chunks.push(value);
          }

          progressLastCall[name] = 0;
          progressLastCall['progress'] = 0;
          if (displayLeaderRef.current === desc) {
            setFileDownload(formatFileSize(totalArchive));
            setFileDownloadProgress(formatFileSize(downloadedArchive));
            updateProgress(downloadedArchive, totalArchive, 'progress');
          }
          updateProgress(downloadedArchive, totalArchive, name);
          if (displayLeaderRef.current === desc) displayLeaderRef.current = null;

          const blob = new Blob(chunks as BlobPart[]);
          console.log(`[${name}] zip size (bytes)`, blob.size);

          const zip = await JSZip.loadAsync(blob);
          const fileEntries = Object.entries(zip.files);
          console.log(`[${name}] entries in zip`, fileEntries.length);
          console.log(`[${name}] sample names`, fileEntries.slice(0, 10).map(([n]) => n));

          let manifestMap: Record<number, string> | null = null;
          const manifestEntry = Object.values(zip.files).find((f) => f.name.toLowerCase().endsWith('.json'));
          if (manifestEntry) {
            try {
              const manifestRaw = await manifestEntry.async('string');
              console.log(`[${name}] manifest raw (first 500 chars)`, manifestRaw.slice(0, 500));
              const manifest = JSON.parse(manifestRaw);
              manifestMap = Array.isArray(manifest)
                ? manifest.reduce((acc: Record<number, string>, item: any) => {
                    if (item?.COD_PRODUTO && item?.imagem) acc[item.COD_PRODUTO] = item.imagem;
                    return acc;
                  }, {})
                : Object.entries(manifest).reduce((acc, [key, val]) => {
                    const num = Number(key);
                    if (Number.isFinite(num)) acc[num] = String(val);
                    return acc;
                  }, {} as Record<number, string>);
              console.log(`[${name}] manifest parsed keys`, Object.keys(manifestMap).length);
            } catch (err) {
              console.warn(`[${name}] manifest parse failed`, err);
              manifestMap = null;
            }
          } else {
            console.log(`[${name}] manifest not found`);
          }

          const imagesDict: Record<number, string> = {};
          let logged = 0;

          for (const [filename, file] of fileEntries) {
            if (file.dir || !/\.(webp|png|jpe?g)$/i.test(file.name)) continue;
            const code = getProductCodeFromFilename(
              manifestMap?.[Number(getProductCodeFromFilename(filename))] || filename
            );
            if (!code) continue;
            const imageBlob = await file.async('blob');
            const base64 = await blobToBase64(imageBlob);
            imagesDict[code] = base64.replace(/^data:.*;base64,/i, '');

            if (logged < 5) {
              console.log(`[${name}] saved sample`, {
                filename,
                code,
                blobSize: imageBlob.size,
                mime: imageBlob.type,
              });
              logged++;
            }
          }

          console.log(`[${name}] total images mapped`, Object.keys(imagesDict).length);
          downloadEvents[name] = { ...(downloadEvents[name] || {}), dataFim: getDateTimeMS(), status: 1 };

          return { data: imagesDict, name };
        } catch (error) {
          console.warn(`Falha no ZIP de ${name}, tentando JSON. Motivo:`, error);
          downloadEvents[name] = { ...(downloadEvents[name] || {}), dataFim: getDateTimeMS(), status: 0 };
          return fetchAndSaveJson(url, name, desc, true);
        }
      };

      const tasks = (list ?? []).map(
        ({ TIPO_ARQUIVO, DESC_ARQUIVO, URL_S3 }) =>
          () =>
            fetchAndSaveJson(URL_S3, TIPO_ARQUIVO, DESC_ARQUIVO)
      );

      setDownloadingFiles((list ?? []).map((item) => item.DESC_ARQUIVO));
      
      const responses = await runWithConcurrency(tasks, MAX_CONCURRENT_DOWNLOADS);
      const concatDownloadEvents = Object.values(downloadEvents).map(evt => `${evt.status} ${evt.arquivo} ${evt.dataInicio?.split('T')[1] || ''}-${evt.dataFim?.split('T')[1] || ''}`).join('|');
      
      await registerEvent({
        tipoEvento: 'DOWNLOAD',
        obsEvento: concatDownloadEvents,
        stEvento: Object.values(downloadEvents).some(evt => evt.status === 0) ? 0 : 1,
        codRca: Number(getOnStorage('cod_pessoa') || 0),
      });

      let fulfilled: Record<string, any> = {};
      const rejected: any[] = [];
      responses.forEach((item) => {
        if (item.status === 'fulfilled') fulfilled[item.value.name] = item.value;
        if (item.status === 'rejected') rejected.push(item.reason);
      });

      if (rejected.length > 0) {
        console.warn('Promises rejeitadas:', rejected);
      }

      setDownloadSpeedBpsRef.current(0);
      return Object.keys(fulfilled).length ? fulfilled : undefined;
    },
    [getOnDB] 
  );

  const tryCatch = async <T>(fn: () => Promise<T>, msg: string): Promise<T | undefined> => {
    try {
      await fn()
    } catch (error) {
      await registerEvent({
        tipoEvento: 'PROCESSAMENTO',
        obsEvento: `Erro ao carregar [${msg}]: ${error}`,
        stEvento: 0,
      });
      return undefined;
    }
    await yieldToMain();
  };

  const loadFiles = useCallback(
    async (newDatasets?: DataSetsProps[]) => {
      if (loading.current) return;

      loading.current = true;
      setError(false);
      setIsSuccess(false);
      updateProgress(0, 0, '.');

      const listToDownload = (newDatasets || []).map(d => ({
        ...d,
        TIPO_ARQUIVO: normalizeDatasetType(d.TIPO_ARQUIVO),
      }));
      if (!listToDownload.length) {
        setNoUpdate(true);
        loading.current = false;
        return;
      }

      if (listToDownload.find((i) => i.TIPO_ARQUIVO === 'estoque')) {
        removeFromStorage('stock_left');
      }

      const resp = await fetchDatasets(listToDownload);

      await registerEvent({
        tipoEvento: 'PROCESSAMENTO',
        obsEvento: 'Iniciando processamento de dados',
      });
      
      // Pega a lista antiga de datasets
      const oldDatasets = getOnStorage('datasets') || [];
      // Pega os datasets que foram baixados com sucesso
      const successfullyDownloaded = listToDownload.filter((d) => resp && resp[d.TIPO_ARQUIVO]);

      // Cria um mapa da lista antiga para fácil substituição
      const oldDatasetsMap = oldDatasets.reduce(
        (acc, item) => {
          acc[item.TIPO_ARQUIVO] = item;
          return acc;
        },
        {} as Record<string, DataSetsProps>
      );

      // Atualiza o mapa com os arquivos baixados com sucesso
      successfullyDownloaded.forEach((item) => {
        oldDatasetsMap[item.TIPO_ARQUIVO] = item;
      });

      // Converte o mapa de volta para uma lista e salva
      const finalDatasetList = Object.values(oldDatasetsMap);
      setOnStorage('datasets', finalDatasetList);
      setOnStorage('new_data_sets', finalDatasetList);
      setDataSets(listToDownload);

      setPreparing(true);

      const getData = async <T extends Tables>(dbKey: T, defaultValue: any): Promise<TableTypes[T]> => {
        if (resp?.[dbKey]?.data != null) return resp[dbKey].data as TableTypes[T];
        const fromDb = await getOnDB(dbKey);
        return (fromDb ?? defaultValue) as TableTypes[T];
      };

      const getPrecoData = async () => {
        // Prioriza novo dataset de preço e mantém fallback para o legado.
        if (resp?.preco_novo?.data != null) return resp.preco_novo.data;
        return getData('preco', []);
      };

      const parseNumberLoose = (value: any): number | null => {
        if (value === null || value === undefined) return null;
        if (typeof value === 'number') return Number.isFinite(value) ? value : null;
        if (typeof value === 'string') {
          const trimmed = value.trim();
          if (!trimmed) return null;
          // Tenta lidar com valores com ',' (pt-BR) e '.' (milhares)
          const normalized = trimmed.includes(',') ? trimmed.replace(/\./g, '').replace(',', '.') : trimmed;
          const num = Number(normalized);
          return Number.isFinite(num) ? num : null;
        }
        return null;
      };

      // Saldo flex (novo formato):
      // { [COD_PESSOA]: { VLR_ERP } }
      const mapSaldoFlexToNewMap = (src: any): Record<string, { VLR_ERP: number | null }> => {
        if (!src) return {};

        // Alguns backends podem trazer wrapper: { data: {...} } ou { data: { data: {...} } }
        const candidates: any[] = [src, src?.data, src?.data?.data].filter(Boolean);
        const mapRoot = candidates.find((c) => {
          if (!c || typeof c !== 'object' || Array.isArray(c)) return false;
          const keys = Object.keys(c);
          return keys.length > 0 && keys.some((k) => Number.isFinite(Number(k)));
        });

        if (!mapRoot) return {};

        return Object.entries(mapRoot).reduce<Record<string, { VLR_ERP: number | null }>>((acc, [cod, node]) => {
          const codPessoa = Number(cod);
          if (!Number.isFinite(codPessoa)) return acc;

          // novo formato: { [COD_PESSOA]: { VLR_ERP } }
          // tolera também caso venha { [COD_PESSOA]: { VLR_ERP: '...' } } ou valor direto
          const vlrRaw =
            node && typeof node === 'object' ? (node as any).VLR_ERP ?? node : null;

          const parsed = parseNumberLoose(vlrRaw);
          acc[String(codPessoa)] = { VLR_ERP: parsed };
          return acc;
        }, {});
      };

      const getSaldoFlexData = async (): Promise<TableTypes['saldo_flex']> => {
        // Prioriza o novo dataset, e salva diretamente no `saldo_flex` (objeto por COD_PESSOA).
        // Não suportamos mais o formato legado (array).
        const srcNovo = resp?.saldo_flex_novo?.data;
        if (srcNovo != null) return mapSaldoFlexToNewMap(srcNovo);

        const srcFlex = resp?.saldo_flex?.data;
        if (srcFlex != null && !Array.isArray(srcFlex)) return mapSaldoFlexToNewMap(srcFlex);

        const fromDb = await getOnDB('saldo_flex');
        if (!fromDb || Array.isArray(fromDb)) return {};
        return mapSaldoFlexToNewMap(fromDb);
      };

      const [
        precos,
        marcas,
        imagens,
        estoque,
        catalogo,
        saldoFlex,
        loadedClients,
        categorias,
        clienteBoletos,
        clienteEventosCortes,
        indicadores,
        campanha,
        listPageBuilder,
        janelaEntrega,
        parametrosAws,
        indicadorLoja,
        campanhaFaixa,
        campanhaProduto,
        campanhaCliente,
        campanhaTopMix,
        parametroPrecoBase,
        recomendacaoBigData,
        precoPromocaoCliente,
        cicloVendaCliente,
        percentualFornecedor,
        substituicaotributaria,
        restricaoProdutoCliente,
        ultimaCompra,
      ] = await Promise.all([
        getPrecoData(),
        getData('marcas', []),
        getData('imagens', {}),
        getData('estoque', []),
        getData('catalogo', []),
        getSaldoFlexData(),
        getData('cliente', []),
        getData('categoria', []),
        getData('cliente_boletos', {}),
        getData('cliente_cortes', {}),
        getData('indicadores', []),
        getData('campanha_indicador', []),
        getData('pagebuilder', []),
        getData('janela_entrega', []),
        getData('parametros_aws', []),
        getData('indicador_loja', []),
        getData('campanha_faixa', []),
        getData('campanha_produto', {}),
        getData('campanha_cliente', {}),
        getData('campanha_mix_fornecedor', []),
        getData('parametro_preco_base', []),
        getData('recomendacaoBigData', []),
        getData('precoPromocaoCliente', []),
        getData('ciclo_venda_cliente_cliente', []),
        getData('percentual_fornecedor', []),
        getData('cod_regime_tributario', []),
        getData('restricaoProdutoCliente', []),
        getData('ultima_compra', []),
      ]);
      

      // Define origem dos dados de preço:
      // - prioriza dataset preco_novo
      // - fallback para preço legado salvo no DB
      const precosSource = precos ?? [];

      const yieldToMain = (): Promise<void> =>
        new Promise((resolve) => {
          if (typeof requestIdleCallback !== 'undefined') {
            requestIdleCallback(() => resolve(), { timeout: 50 });
          } else {
            setTimeout(resolve, 0);
          }
        });

      // Trabalhando com o dataset de eventos do cliente [clienteBoletos]
      await tryCatch(async () => {
        eventos =
          clienteBoletos?.clientes?.reduce((acc, cliente) => {
            return {
              ...acc,
              [cliente.cod_cliente]: {
                boletos: cliente.boletos,
                cod_cliente: cliente.cod_cliente,
              },
            };
          }, {} as EventsClientProps) || {};
      }, 'clienteBoletos');

      // Trabalhando com o dataset de ciclo de venda do cliente [cicloVendaCliente]
      await tryCatch(async () => {
        if (Array.isArray(cicloVendaCliente)) {
          for (let j = 0; j < cicloVendaCliente.length; j++) {
            const cycle = cicloVendaCliente[j];
            const cod = cycle.COD_CLIENTE;
            if (!cycleByClient[cod]) cycleByClient[cod] = [];
            cycleByClient[cod].push(cycle);
          }
          Object.keys(cycleByClient).forEach((cod) => {
            cycleByClient[Number(cod)].sort((a, b) => a.SEMANA_CICLO_ID - b.SEMANA_CICLO_ID);
          });
        }
      }, 'cicloVendaCliente')
  
      // Trabalhando com o dataset de clientes [loadedClients]
      await tryCatch(async () => {
        sortedClients = loadedClients?.sort((a, b) => {
          if (a.NM_FANTASIA > b.NM_FANTASIA) return 1;
          if (a.NM_FANTASIA < b.NM_FANTASIA) return -1;
          return 0;
        }) || [];
      }, 'loadedClients');

      // Trabalhando com o dataset de clientes [clientes]
      await tryCatch(async () => {
        for (let i = 0; i < sortedClients.length; i += CLIENTS_CHUNK_SIZE) {
          const chunk = sortedClients.slice(i, i + CLIENTS_CHUNK_SIZE);
          chunk.forEach((c) => {
            c.COD_SEGMENTO = parseJSON(c?.COD_SEGMENTO || '[]');
            c.DT_ULTIMA_COMPRA = parseJSON(c?.DT_ULTIMA_COMPRA || '[]');
            c.UNILEVER = c.FL_UNILEVER === 'True';
            const CYCLE = cycleByClient[c.COD_CLIENTE] || [];
            clientes[c.COD_CLIENTE] = { ...c, CYCLE };
          });
          await yieldToMain();
        }
      }, 'clientes');

      // Trabalhando com o dataset de preços [precos]
      await tryCatch(async () => {
        const src: any = precosSource;
        if (!src || typeof src !== 'object') {
          mapedPrices = {};
          return;
        }

        const isLikelyPriceItem = (v: any) => {
          if (!v || typeof v !== 'object') return false;
          // Campos mais comuns no payload de preço
          return (
            v.VLR_PRECO != null ||
            v.VLR_PAUTA != null ||
            v.DESTACA_ST != null ||
            v.COD_TRIBUTACAO_PRODUTO != null ||
            v.COD_PROMOCAO != null
          );
        };

        const out: Record<string, any[]> = {};

        // Formato antigo:
        //   { PRODUTOS: { [COD_PRODUTO]: { [QTD_EMBALAGEM]: PriceItem } }, FILTRO: {...} }
        if (src.PRODUTOS && typeof src.PRODUTOS === 'object') {
          const produtos = src.PRODUTOS || {};
          const filtro = src.FILTRO || {};

          Object.entries(produtos).forEach(([codProd, embalagens]) => {
            out[codProd] = out[codProd] || [];
            Object.entries(embalagens as any).forEach(([qtdEmb, preco]) => {
              const precoItem = preco as any;
              out[codProd].push({
                ...precoItem,
                COD_PRODUTO: Number(codProd),
                QTD_EMBALAGEM: Number(qtdEmb),
                COD_LOJA: filtro.COD_LOJA ?? null,
                NRO_SEGMENTO: filtro.NRO_SEGMENTO ?? null,
                TP_PROMO: filtro.TP_PROMO ?? precoItem.TP_PROMO ?? null,
                FAIXAACRFINANCEIRO: filtro.FAIXAACRFINANCEIRO ?? precoItem.FAIXAACRFINANCEIRO ?? null,
                INDUSAACRESCTABVENDA: filtro.INDUSAACRESCTABVENDA ?? precoItem.INDUSAACRESCTABVENDA ?? null,
              });
            });
          });

          mapedPrices = out;
          return;
        }

        // Formato novo (exemplo do usuário):
        //   { data: { [COD_LOJA]: { [NRO_SEGMENTO]: { [COD_PRODUTO]: { [QTD_EMBALAGEM]: PriceItem } } } } }
        // Além disso, o dataset pode vir com wrapper tipo: { data: {...}, keyPath: 'preco', preco: 1 }
        const candidates: any[] = [src.data, src.data?.data, src];
        const dataRoot = candidates.find((c) => {
          if (!c || typeof c !== 'object') return false;
          const keys = Object.keys(c);
          return keys.some((k) => !Number.isNaN(Number(k)) && k !== 'keyPath' && k !== 'preco');
        });

        if (!dataRoot || typeof dataRoot !== 'object') {
          mapedPrices = {};
          return;
        }

        Object.entries(dataRoot as any).forEach(([codLoja, bySegment]) => {
          if (!bySegment || typeof bySegment !== 'object') return;

          Object.entries(bySegment as any).forEach(([nroSegmento, byProduct]) => {
            if (!byProduct || typeof byProduct !== 'object') return;

            Object.entries(byProduct as any).forEach(([codProd, priceNode]) => {
              if (!priceNode || typeof priceNode !== 'object') return;

              const codLojaNum = Number(codLoja);
              const segNum = Number(nroSegmento);
              if (!Number.isFinite(codLojaNum) || !Number.isFinite(segNum)) return;

              // priceNode costuma ser { [QTD_EMBALAGEM]: PriceItem }
              const embEntries = Object.entries(priceNode as any);
              if (embEntries.length === 0) return;

              const [firstKey] = embEntries[0];
              const firstKeyNum = Number(firstKey);

              // No dataset novo, as chaves de priceNode são quantidades de embalagem.
              if (!Number.isNaN(firstKeyNum)) {
                embEntries.forEach(([qtdEmb, preco]) => {
                  const qtdEmbNum = Number(qtdEmb);
                  if (Number.isNaN(qtdEmbNum) || !preco) return;

                  out[codProd] = out[codProd] || [];
                  out[codProd].push({
                    ...(preco as any),
                    COD_PRODUTO: Number(codProd),
                    QTD_EMBALAGEM: qtdEmbNum,
                    COD_LOJA: codLojaNum,
                    NRO_SEGMENTO: segNum,
                  });
                });
              } else if (isLikelyPriceItem(priceNode)) {
                // Caso raro: priceNode já seja um item único (não mapeado por QTD)
                const qtdEmbNum =
                  (priceNode as any).QTD_EMBALAGEM != null
                    ? Number((priceNode as any).QTD_EMBALAGEM)
                    : (priceNode as any).QTD != null
                      ? Number((priceNode as any).QTD)
                      : null;

                if (qtdEmbNum != null && Number.isFinite(qtdEmbNum)) {
                  out[codProd] = out[codProd] || [];
                  out[codProd].push({
                    ...(priceNode as any),
                    COD_PRODUTO: Number(codProd),
                    QTD_EMBALAGEM: qtdEmbNum,
                    COD_LOJA: codLojaNum,
                    NRO_SEGMENTO: segNum,
                  });
                }
              }
            });
          });
        });

        mapedPrices = out;
      }, 'precos');

      // Trabalhando com o dataset de preços promocionais [precoPromocaoCliente]
      await tryCatch(async () => {
        promocaoCliente = Array.isArray(precoPromocaoCliente)
          ? precoPromocaoCliente.reduce((acc: PrecoPromocaoClienteProps, p: PromocaoClientProps) => {
              const clientPrice = acc[p.COD_CLIENTE];

              if (!clientPrice) {
                acc[p.COD_CLIENTE] = { [p.QTD_EMBALAGEM]: { [p.COD_PRODUTO]: { [p.COD_LOJA]: { [p.NRO_SEGMENTO]: p } } } };
                return acc;
              }

              const qtdEmbalagem = clientPrice[p.QTD_EMBALAGEM];
              if (!qtdEmbalagem) {
                acc[p.COD_CLIENTE][p.QTD_EMBALAGEM] = { [p.COD_PRODUTO]: { [p.COD_LOJA]: { [p.NRO_SEGMENTO]: p } } };
                return acc;
              }

              const productPrice = qtdEmbalagem[p.COD_PRODUTO];
              if (!productPrice) {
                acc[p.COD_CLIENTE][p.QTD_EMBALAGEM][p.COD_PRODUTO] = { [p.COD_LOJA]: { [p.NRO_SEGMENTO]: p } };
                return acc;
              }

              const productPriceLoja = productPrice[p.COD_LOJA];
              if (!productPriceLoja) {
                acc[p.COD_CLIENTE][p.QTD_EMBALAGEM][p.COD_PRODUTO][p.COD_LOJA] = { [p.NRO_SEGMENTO]: p };
                return acc;
              }

              const productPriceSegmento = productPriceLoja[p.NRO_SEGMENTO];
              if (!productPriceSegmento) {
                acc[p.COD_CLIENTE][p.QTD_EMBALAGEM][p.COD_PRODUTO][p.COD_LOJA][p.NRO_SEGMENTO] = p;
                return acc;
              }

              if (p.VLR_PRECO < productPriceSegmento.VLR_PRECO) {
                acc[p.COD_CLIENTE][p.QTD_EMBALAGEM][p.COD_PRODUTO][p.COD_LOJA][p.NRO_SEGMENTO] = p;
                return acc;
              }

              return acc;
            }, {})
          : precoPromocaoCliente;
      }, 'precoPromocaoCliente');

      // Trabalhando com o dataset de última compra [ultimaCompra]
      await tryCatch(async () => {
        if (Array.isArray(ultimaCompra)) {
          for (let j = 0; j < ultimaCompra.length; j++) {
            const c = ultimaCompra[j];
            productUltimaCompra[c.SEQPRODUTO] = c;
          }
        }
      }, 'ultimaCompra');

      // Trabalhando com o dataset de produtos [catalogo]
      await tryCatch(async () => {
        for (let i = 0; i < catalogo.length; i += CATALOG_CHUNK_SIZE) {
          const chunk = catalogo.slice(i, i + CATALOG_CHUNK_SIZE);
          chunk.forEach((item: ProductProps) => {
            const PRECO = mapedPrices[item.COD_PRODUTO];
            if (!PRECO?.length) return;

            const EMBALAGEM = item?.EMBALAGEM?.map((emb) => {
              if (emb?.QTD_MULTIPLO_VENDA > 1) {
                return { ...emb, SG_EMBALAGEM: 'PK' };
              }
              return { ...emb };
            });

            const product = {
              ...item,
              PRECO,
              IMAGEM: '',
              EMBALAGEM,
              UNILEVER: item.FL_UNILEVER === 'True',
              ultimaCompra: productUltimaCompra[item.COD_PRODUTO],
            } as ProductProps;

            products.push(product);
            objProduct[item.COD_PRODUTO] = product;
          });
          await yieldToMain();
        }
      }, 'catalogo');

      // Trabalhando com o dataset de categorias [categorias]
      await tryCatch(async () => {
        if (!!categorias?.length) {
          categorias?.forEach((item: CategoryProps) => {
            if (!objCategory[item?.COD_CATEGORIA]) {
              objCategory[item?.COD_CATEGORIA] = {
                ...item,
              };
            }
          });
        }
      }, 'categorias');
    
      // Trabalhando com o dataset de categorias [allCategories]
      await tryCatch(async () => {
        isLoaded = !!categorias.find((c) => {
          return c.PATH_CATEGORIA === campanhaProduto?.url_amigavel;
        });
        allCategories = [...(isLoaded ? [] : mountCategories(campanhaProduto)), ...categorias];
      }, 'allCategories');

      // Trabalhando com o dataset de pagebuilder [listPageBuilder]
      await tryCatch(async () => {
        pageBuilder = listPageBuilder.map((i: any, ind: any) => ({ ...i, ID: toCoded(`${ind + 1}`, true, true) }));
      }, 'listPageBuilder');

      await registerEvent({
        tipoEvento: 'PROCESSAMENTO',
        obsEvento: 'Finalizando processamento de dados',
      });

      // dados | chave | deve validar | é obrigatório
      const dbWrites: [data: any, key: Tables, shouldValidate?: boolean, isObrigatory?: boolean][] = [
        [marcas, 'marcas', true, true],
        [precosSource, 'preco', true, true],
        [estoque, 'estoque', true, true],
        [imagens, 'imagens', true],
        [catalogo, 'catalogo', true, true],
        [Object.values(clientes), 'cliente', true, true],
        [products, 'produtos'],
        [saldoFlex, 'saldo_flex', true],
        [pageBuilder, 'pagebuilder', true],
        [indicadores, 'indicadores', true],
        [allCategories, 'categoria', true, true],
        [campanha, 'campanha_indicador', true],
        [parametrosAws, 'parametros_aws', true],
        [janelaEntrega, 'janela_entrega', true],
        [campanhaFaixa, 'campanha_faixa', true],
        [indicadorLoja, 'indicador_loja', true],
        [objProduct, 'dicionario_produtos'],
        [eventos, 'eventos'],
        [campanhaCliente, 'campanha_cliente', true],
        [campanhaProduto, 'campanha_produto', true],
        [objCategory, 'dicionario_categorias'],
        [promocaoCliente, 'precoPromocaoCliente', true],
        [campanhaTopMix, 'campanha_mix_fornecedor', true],
        [parametroPrecoBase, 'parametro_preco_base', true, true],
        [recomendacaoBigData, 'recomendacaoBigData', true],
        [percentualFornecedor, 'percentual_fornecedor', true, true],
        [substituicaotributaria, 'cod_regime_tributario', true, true],
        [cicloVendaCliente, 'ciclo_venda_cliente_cliente', true],
        [restricaoProdutoCliente, 'restricaoProdutoCliente', true],
        [ultimaCompra, 'ultima_compra', true],
        [clienteEventosCortes, 'cliente_cortes', true],
        [clienteBoletos, 'cliente_boletos', true],
      ];

      const relatorio: { key: Tables; st: number; obs?: string }[] = [];
      let hasError = false;

      // dados | chave | deve validar | é obrigatório
      const runOneDbWrite = async ([data, key, shouldValidate, isObrigatory]: (typeof dbWrites)[number]) => {
        try {
          await setOnDB(data, key).catch((err: any) => {
            relatorio.push({ key, st: 0, obs: `Erro ao salvar dados em ${key}: ${err?.message ?? err}` });
            hasError = true;
          });
          if (shouldValidate) {
            const total = (Array.isArray(data) ? data.length : Object.keys(data).length) || 0;
            if (total === 0) {
              const obrigatorio = isObrigatory ? 'O' : 'N';
              relatorio.push({ key, st: 0, obs: `sem dados {${obrigatorio}}` });
              hasError = true;
            } else {
              relatorio.push({ key, st: 1 });
            }
          }
        } catch (err: any) {
          relatorio.push({ key, st: 0, obs: `Erro no banco de dados ao salvar dados em ${key}: ${err?.message ?? err}` });
          hasError = true;
        }
      };

      // dados | chave | deve validar | é obrigatório
      for (let b = 0; b < dbWrites.length; b += SET_DB_BATCH_SIZE) {
        const batch = dbWrites.slice(b, b + SET_DB_BATCH_SIZE);
        if (SEQUENTIAL_IDB_BATCH) {
          for (const entry of batch) {
            await runOneDbWrite(entry);
            await yieldToMain();
          }
        } else {
          await Promise.all(batch.map((entry) => runOneDbWrite(entry)));
          await yieldToMain();
        }
      }

      await registerEvent({
        tipoEvento: 'DB_FUNCTIONS',
        obsEvento: `Salvando dados: ${relatorio.map((item) => {
          const obs = item.obs ? `[${item.obs}]` : '';
          return `${item.st} ${item.key} ${obs}`
        }).join('|')}`,
        stEvento: hasError ? 0 : 1,
      });

      if (downloadLabelTimeoutRef.current) {
        clearTimeout(downloadLabelTimeoutRef.current);
        downloadLabelTimeoutRef.current = null;
      }
      
      const rcaCustomer = getOnStorage('rca_usuario');

      setCustomer(rcaCustomer as CustomerDataProps);
      setCampanhaProdutos(campanhaProduto || {});
      setCampanhaClientes(campanhaCliente || {});
      setCampanhaFaixa(campanhaFaixa || []);
      setCampanhaTopMix(campanhaTopMix || []);
      setIndicadorLoja(indicadorLoja || []);
      setIndicadoresProdutividade(indicadores || []);

      setPreparing(false);
      setIsSuccess(true);
      loading.current = false;

      return resp;
    },
    [
      fetchDatasets,
      getOnDB,
      setDownloadingFileName,
      setOnDB,
      setCustomer,
      setCampanhaProdutos,
      setCampanhaClientes,
      setCampanhaFaixa,
      setCampanhaTopMix,
      setIndicadorLoja,
      setIndicadoresProdutividade,
    ]
  );

  const updateStock = useCallback(async () => {
    try {
      const rcaInfos = getOnStorage('rca_usuario');
      if (!rcaInfos?.codPessoa) return;

      const resp: any = await axiosCtx.get(`/getdataset/${rcaInfos.codPessoa}`, { timeout: GETDATASET_STOCK_TIMEOUT_MS });
      const [representante] = resp?.data?.representante || [];
      const newList: DataSetsProps[] = representante?.link_Dataset || [];

      if (!newList.length) return;

      const oldList: DataSetsProps[] = getOnStorage('datasets') || [];
      const newStockFile = newList.find((item) => item.TIPO_ARQUIVO === 'estoque');
      const oldStockFile = oldList.find((item) => item.TIPO_ARQUIVO === 'estoque');

      if (newStockFile && newStockFile.URL_S3 !== oldStockFile?.URL_S3) {
        const stockResponse = await fetchDatasets([newStockFile]);

        if (stockResponse?.estoque?.data) {
          await setOnDB(stockResponse.estoque.data, 'estoque');
          const updatedOldList = oldList.filter((item) => item.TIPO_ARQUIVO !== 'estoque');
          const updatedDataSet = [...updatedOldList, newStockFile];

          setOnStorage('datasets', updatedDataSet);
          setOnStorage('stock_left', {});
        }
      }
    } catch (error) {
      console.error('Erro ao atualizar o estoque:', error);
      setError(true);
    }
  }, [axiosCtx, fetchDatasets, setOnDB]);

  const getLinks = useCallback(
    async ({ isBackground }: { isBackground?: boolean } = { isBackground: false }) => {
      setPreparing(true);
      setError(false);
      setIsSuccess(false);

      const rcaInfos = getOnStorage('rca_usuario');
      const codPessoa = rcaInfos?.codPessoa ?? rcaInfos?.cod_pessoa;
      if (!codPessoa) {
        setPreparing(false);
        setError(true);
        return;
      }

      if (!isBackground) {
        try {
          localStorage.setItem('login_bootstrap', 'in-progress');
        } catch {}
      }

      const resp: any = await axiosCtx.get(`/getdataset/${codPessoa}`, { timeout: GETDATASET_TIMEOUT_MS }).catch(() => 'error');

      if (resp === 'error') {
        setPreparing(false);
        setError(true);
        return;
      }

      const [representante] = resp?.data?.representante || [];
      const list = representante?.link_Dataset || [];

      const oldList: DataSetsProps[] = getOnStorage('datasets') || [];
      const urlSet = new Set(oldList.map((i) => i.URL_S3));
      let filteredList = list.filter((dt: DataSetsProps) => !urlSet.has(dt.URL_S3));

      const CRITICAL_TABLES: Tables[] = tableKeys as unknown as Tables[];
      if (filteredList.length === 0 && oldList.length > 0) {
        const listByType = list.reduce((acc: Record<string, DataSetsProps>, i: DataSetsProps) => {
          acc[i.TIPO_ARQUIVO] = i;
          return acc;
        }, {});
        const toCheck = oldList.filter(
          (el) => tableKeys.includes(el.TIPO_ARQUIVO as Tables) && CRITICAL_TABLES.includes(el.TIPO_ARQUIVO as Tables)
        );

        const emptyTables = await Promise.all(
          toCheck.map(async (element) => {
            const item = await getOnDB(element.TIPO_ARQUIVO as Tables);
            const isFill = Array.isArray(item) ? !!item?.length : !!Object.keys(item || {}).length;
            return isFill ? null : (listByType[element.TIPO_ARQUIVO] ?? null);
          })
        );
        const toAdd = emptyTables.filter((el): el is DataSetsProps => el != null);
        filteredList = [...filteredList, ...toAdd];
      }

      const data = await loadFiles(filteredList);

      const formattedDate = getDate();
      setLastSync(formattedDate);
      // Concluiu com sucesso o bootstrap de login (download dos datasets necessários)
      if (!isBackground) {
        try {
          localStorage.setItem('login_bootstrap', 'done');
        } catch {}
      }

      if (isBackground) return filteredList;

      setTimeout(() => {
        unregisterServiceWorker({ preserveLocalStorage: isBackground });
      }, 100);
      return data;
    },
    [loadFiles, setLastSync, getDate, axiosCtx]
  );

  return {
    loading: loading.current,
    hasError,
    noUpdate,
    fileName,
    datasets,
    preparing,
    isSuccess,
    isOpenedModal,
    downloadSpeedBps,

    setError,
    loadFiles,
    getLinks,
    updateStock,
    openModalSync,
  };
};
